/**
 * Lab payment adapter — normalizes PaymentStep.getPaymentData() output
 * into the lab backend payment contract.
 *
 * This adapter sits between PaymentStep and the lab checkout submit.
 * It does NOT modify PaymentStep or the normal questionnaire checkout.
 */

/**
 * Generate a durable idempotency key for the lab checkout session.
 * Reuses the same key on retry within the same browser session.
 *
 * @param {{ slug: string, patientId: string, total: number|string }} context
 * @returns {string}
 */
export function getLabIdempotencyKey({ slug, patientId, total }) {
  const storageKey = `lab-checkout-idem-${slug}-${patientId}-${total}`;
  let key = sessionStorage.getItem(storageKey);
  if (!key) {
    key = `lab-${slug}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(storageKey, key);
  }
  return key;
}

/**
 * Clear the idempotency key after a successful checkout.
 *
 * @param {{ slug: string, patientId: string, total: number|string }} context
 */
export function clearLabIdempotencyKey({ slug, patientId, total }) {
  const storageKey = `lab-checkout-idem-${slug}-${patientId}-${total}`;
  sessionStorage.removeItem(storageKey);
}

/**
 * Normalize the raw PaymentStep.getPaymentData() output into the lab
 * backend payment contract shape.
 *
 * @param {object|null} rawPayment - Output from PaymentStep.getPaymentData()
 * @param {{ slug: string, patientId: string, total: number|string }} context
 * @returns {object} Normalized payment payload for lab checkout POST
 */
export function normalizeLabPaymentPayload(rawPayment, { slug, patientId, total }) {
  if (!rawPayment || !rawPayment.gateway || !rawPayment.payment_data) {
    return null;
  }

  const gateway = rawPayment.gateway === "authorizenet" ? "authorize_net" : rawPayment.gateway;
  const paymentData = rawPayment.payment_data || {};
  const idempotencyKey = getLabIdempotencyKey({ slug, patientId, total });

  // Normalize gateway name: backend accepts "stripe", "nmi", or "authorize_net".
  const processor = gateway;

  return {
    gateway: processor,
    processor,
    payment_data: {
      // Stripe fields
      ...(paymentData.payment_method_id && { payment_method_id: paymentData.payment_method_id }),
      ...(paymentData.paymentMethodId && { payment_method_id: paymentData.paymentMethodId }),

      // Saved card (all gateways)
      ...(paymentData.saved_payment_method_id && { saved_payment_method_id: paymentData.saved_payment_method_id }),
      ...(paymentData.payment_method_record_id && { payment_method_record_id: paymentData.payment_method_record_id }),

      // NMI token
      ...(paymentData.payment_token && { payment_token: paymentData.payment_token }),
      ...(paymentData.token && !paymentData.payment_token && { payment_token: paymentData.token }),

      // Authorize.Net opaque data
      ...(paymentData.opaqueDataDescriptor && { opaqueDataDescriptor: paymentData.opaqueDataDescriptor }),
      ...(paymentData.opaqueDataValue && { opaqueDataValue: paymentData.opaqueDataValue }),
      ...(paymentData.opaque_data_descriptor && { opaque_data_descriptor: paymentData.opaque_data_descriptor }),
      ...(paymentData.opaque_data_value && { opaque_data_value: paymentData.opaque_data_value }),
      ...(paymentData.dataDescriptor && !paymentData.opaqueDataDescriptor && { opaqueDataDescriptor: paymentData.dataDescriptor }),
      ...(paymentData.dataValue && !paymentData.opaqueDataValue && { opaqueDataValue: paymentData.dataValue }),

      // Authorize.Net raw card data
      ...(paymentData.raw_card_data && { raw_card_data: true }),
      ...(paymentData.card_number && { card_number: paymentData.card_number }),
      ...(paymentData.exp_month && { exp_month: paymentData.exp_month }),
      ...(paymentData.exp_year && { exp_year: paymentData.exp_year }),
      ...(paymentData.cvv && { cvv: paymentData.cvv }),

      // Billing fields (all gateways)
      ...(paymentData.postal_code && { postal_code: paymentData.postal_code }),
      ...(paymentData.billing_postal_code && !paymentData.postal_code && { postal_code: paymentData.billing_postal_code }),
      ...(paymentData.cardholder_name && { cardholder_name: paymentData.cardholder_name }),
      ...(paymentData.first_name && { first_name: paymentData.first_name }),
      ...(paymentData.last_name && { last_name: paymentData.last_name }),
      ...(paymentData.address1 && { address1: paymentData.address1 }),
      ...(paymentData.billing_address && { billing_address: paymentData.billing_address }),
      ...(paymentData.city && { city: paymentData.city }),
      ...(paymentData.billing_city && { billing_city: paymentData.billing_city }),
      ...(paymentData.state && { state: paymentData.state }),
      ...(paymentData.billing_state && { billing_state: paymentData.billing_state }),
      ...(paymentData.country && { country: paymentData.country }),
      ...(paymentData.billing_country && { billing_country: paymentData.billing_country }),
      ...(paymentData.phone && { phone: paymentData.phone }),
      ...(paymentData.email && { email: paymentData.email }),

      // NMI card meta
      ...(paymentData.card_meta && { card_meta: paymentData.card_meta }),
    },
    idempotency_key: idempotencyKey,
  };
}
